import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, RotateCcw, Loader2, ExternalLink } from "lucide-react";
import { refreshSiteImages } from "@/hooks/useSiteImages";

const BUILT_IN = {
  hero: "https://customer-assets-cm19k8pv.emergentagent.net/job_care-cert-hub/artifacts/15899d471051a790_assisted.jpg",
  oral_champion: "https://static.prod-images.emergentagent.com/jobs/ad91364e-1619-4fc4-bcc3-13d0678bb749/images/13dc9cd0d62a69fa98f09784990df89326a07cc1963080c3c39e65757a174262.jpeg",
  experts: "https://customer-assets-cm19k8pv.emergentagent.net/job_care-cert-hub/artifacts/f6225276dcc7be7d_The%20Photography%20Lounge%20%28high%20res%29%20040.webp",
  train_the_trainer: "https://customer-assets-cm19k8pv.emergentagent.net/job_care-cert-hub/artifacts/1bbedb18ede23ca6_AaE-seOx4Qd1p7Y_2P8-fg-AaE-seOxdn-yHPGxNMI0Lg.jpg",
  oral_champion_page: "https://customer-assets-cm19k8pv.emergentagent.net/job_care-cert-hub/artifacts/de285c2b4e9300a2_me%20webinar.jpeg",
};

export default function SiteImagesAdmin() {
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState("");
  const refs = useRef({});

  const load = () => api.get("/admin/site-images").then((r) => setSlots(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (slot, file) => {
    if (!file) return;
    setBusy(slot);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/site-images/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Image updated — live on the website now"); refreshSiteImages(); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(""); if (refs.current[slot]) refs.current[slot].value = ""; }
  };
  const reset = async (slot) => {
    try { await api.delete(`/admin/site-images/${slot}`); toast.success("Reverted to the built-in image"); refreshSiteImages(); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">Swap any website photo yourself. Changes go live immediately — no redeploy needed. Landscape photos around 1500×1000px work best.</p>
        <a href="/" target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="h-3.5 w-3.5 mr-1" />View website</Button></a>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {slots.map((s) => (
          <div key={s.key} data-testid={`site-image-${s.key}`} className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden flex flex-col">
            <div className="h-40 bg-cream grid place-items-center overflow-hidden">
              <img src={s.url || BUILT_IN[s.key]} alt={s.label} className="h-full w-full object-cover" />
            </div>
            <div className="px-4 pt-2 text-[11px] text-muted-foreground">{s.url ? "Custom image (live)" : "Built-in image"}</div>
            <div className="hidden">
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <h4 className="font-heading font-bold text-brand">{s.label}</h4>
              <p className="text-xs text-muted-foreground mt-1 flex-1">{s.hint}</p>
              <input ref={(el) => { refs.current[s.key] = el; }} data-testid={`site-image-input-${s.key}`} type="file" accept="image/*" className="hidden" onChange={(e) => upload(s.key, e.target.files[0])} />
              <div className="flex gap-2 mt-3">
                <Button size="sm" data-testid={`site-image-upload-${s.key}`} disabled={busy === s.key} onClick={() => refs.current[s.key]?.click()} className="rounded-full h-8 flex-1 bg-brand hover:bg-brand-hover text-white">
                  {busy === s.key ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{s.url ? "Replace" : "Upload"}
                </Button>
                {s.url && <Button size="sm" variant="ghost" data-testid={`site-image-reset-${s.key}`} onClick={() => reset(s.key)} className="rounded-full h-8"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reset</Button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
